import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, Crown, Sparkle, Rocket, Buildings, ShieldCheck, UserCircle, LinkSimple, CheckCircle, WarningCircle, WhatsappLogo, Copy, CurrencyInr, House, CalendarCheck, UsersThree, Flag, Sparkle as SparkleAi } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const PLAN_OPTIONS = [
  { value: "free",    label: "Free (Trial → ₹1,149/mo)", color: "text-slate-600" },
  { value: "starter", label: "Starter — ₹3,449/mo",    color: "text-blue-600"  },
  { value: "pro",     label: "Pro — ₹11,449/mo",       color: "text-amber-600" },
  { value: "agency",  label: "Agency — Custom pricing", color: "text-violet-600"},
  { value: "admin",   label: "Admin (internal only)",   color: "text-red-600"   },
];

const PLAN_BADGE = {
  free:    "bg-slate-100 text-slate-600",
  basic:   "bg-teal-100 text-teal-700",
  starter: "bg-blue-100 text-blue-700",
  pro:     "bg-amber-100 text-amber-700",
  agency:  "bg-violet-100 text-violet-700",
  admin:   "bg-red-100 text-red-700",
};

const PLAN_ICON = { free: Sparkle, starter: Rocket, pro: Crown, agency: Buildings, admin: ShieldCheck, basic: Sparkle };

// Timestamps are stored as UTC. Rows written before 2026-09-20 carry no offset,
// which JS would otherwise read as local time and render 5.5h early in IST.
const fmtSubmitted = (iso) => {
  const utc = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(utc);
  return isNaN(d) ? "—" : d.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
};

export default function AdminPanel() {
  const [users, setUsers]       = useState([]);
  const [search, setSearch]     = useState("");
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(null);
  const [pendingPlan, setPendingPlan] = useState({});
  const [pendingTrial, setPendingTrial] = useState({});   // email → trial days (string)

  // Platform stats
  const [stats, setStats]           = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Channel manager queue
  const [cmProps, setCmProps]       = useState([]);
  const [cmLoading, setCmLoading]   = useState(false);
  const [cmCodes, setCmCodes]       = useState({});   // { [propId]: { hotel, room, rate } }
  const [cmSaving, setCmSaving]     = useState(null);

  // Airbnb connection queue
  const [abProps, setAbProps]       = useState([]);
  const [abLoading, setAbLoading]   = useState(false);
  const [abDoing, setAbDoing]       = useState({});   // propId → bool
  const [abForm, setAbForm]         = useState({ email: "", airbnb_user_id: "", airbnb_code: "" });
  const [abRecording, setAbRecording] = useState(false);

  // Smart Pricing (Aiosell dynamic pricing) requests
  const [spProps, setSpProps]       = useState([]);
  const [spLoading, setSpLoading]   = useState(false);
  const [spDoing, setSpDoing]       = useState({});   // propId → bool

  // Bookings a host reported a problem with. Each row is an outstanding promise: the
  // host was told HostFolio would cancel it with the booking site and confirm back.
  const [rpRows, setRpRows]       = useState([]);
  const [rpLoading, setRpLoading] = useState(false);
  const [rpDoing, setRpDoing]     = useState({});   // bookingId -> bool
  const [rpNote, setRpNote]       = useState({});   // bookingId -> note
  const [rpShowDone, setRpShowDone] = useState(false);

  // Gemini consumption. Previously untracked entirely, so "how much AI are we using"
  // could only be answered by digging through Google Cloud.
  const [aiUsage, setAiUsage] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDays, setAiDays] = useState(30);

  // Who is actually using HostFolio. Signup counts said nothing about whether anyone
  // came back; this reads login history that was already on disk.
  const [eng, setEng] = useState(null);
  const [engLoading, setEngLoading] = useState(false);
  const [engDays, setEngDays] = useState(30);

  const [tos, setTos] = useState(null);          // time on site, same window as eng
  const [adopt, setAdopt] = useState(null);      // which features each host has touched
  const [adoptLoading, setAdoptLoading] = useState(false);

  const fetchEngagement = useCallback(async (d) => {
    setEngLoading(true);
    try {
      // Logins and time answer the same question, so one switch drives both.
      const [e, t] = await Promise.all([
        api.get("/admin/engagement", { params: { days: d || 30 } }),
        api.get("/admin/time-on-site", { params: { days: d || 30 } }),
      ]);
      setEng(e.data);
      setTos(t.data);
    } catch { toast.error("Failed to load engagement"); }
    finally { setEngLoading(false); }
  }, []);

  const fetchAdoption = useCallback(async () => {
    setAdoptLoading(true);
    try {
      const { data } = await api.get("/admin/feature-adoption", { params: { days: 90 } });
      setAdopt(data);
    } catch { toast.error("Failed to load feature adoption"); }
    finally { setAdoptLoading(false); }
  }, []);

  const fetchAiUsage = useCallback(async (d) => {
    setAiLoading(true);
    try {
      const { data } = await api.get("/admin/ai-usage", { params: { days: d || 30 } });
      setAiUsage(data);
    } catch { toast.error("Failed to load AI usage"); }
    finally { setAiLoading(false); }
  }, []);

  const fetchReported = useCallback(async (showResolved) => {
    setRpLoading(true);
    try {
      const { data } = await api.get("/admin/reported-bookings",
        { params: { include_resolved: !!showResolved } });
      setRpRows(data);
    } catch { toast.error("Failed to load reported bookings"); }
    finally { setRpLoading(false); }
  }, []);

  const resolveReported = async (b, outcome) => {
    const bid = b.id || b.booking_id;
    const note = (rpNote[bid] || "").trim();
    if (outcome === "refused" && !note) {
      toast.error("Add a note saying why the booking site refused");
      return;
    }
    setRpDoing(d => ({ ...d, [bid]: true }));
    try {
      const { data } = await api.post(`/admin/reported-bookings/${bid}/resolve`, { outcome, note });
      toast.success(data.host_notified
        ? "Closed out, host notified"
        : "Closed out (host had no email or phone on file)");
      setRpNote(n => ({ ...n, [bid]: "" }));
      fetchReported(rpShowDone);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not close this out");
    } finally { setRpDoing(d => ({ ...d, [bid]: false })); }
  };

  const fetchCmPending = useCallback(async () => {
    setCmLoading(true);
    try {
      const { data } = await api.get("/admin/aiosell/pending");
      setCmProps(data);
      const init = {};
      data.forEach(p => {
        init[p.id] = {
          hotel: p.aiosell_hotel_code || "",
          room:  p.aiosell_room_code  || "",
          rate:  p.aiosell_rate_plan_code || "",
        };
      });
      setCmCodes(init);
    } catch { toast.error("Failed to load channel manager queue"); }
    finally { setCmLoading(false); }
  }, []);

  const fetchAbPending = useCallback(async () => {
    setAbLoading(true);
    try {
      const { data } = await api.get("/admin/aiosell/airbnb-pending");
      setAbProps(data);
    } catch { toast.error("Failed to load Airbnb queue"); }
    finally { setAbLoading(false); }
  }, []);

  // Record an authorization for a host who cannot complete the form on their own device.
  const recordAirbnbAuth = async () => {
    setAbRecording(true);
    try {
      const { data } = await api.post("/admin/aiosell/airbnb-record", abForm);
      toast.success(`Recorded on ${data.updated} propert${data.updated === 1 ? "y" : "ies"} for ${data.host_email}`);
      setAbForm({ email: "", airbnb_user_id: "", airbnb_code: "" });
      fetchAbPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record the authorization");
    } finally { setAbRecording(false); }
  };

  const fetchSpRequests = useCallback(async () => {
    setSpLoading(true);
    try {
      const { data } = await api.get("/admin/aiosell/pricing-requests");
      setSpProps(data);
    } catch { toast.error("Failed to load Smart Pricing requests"); }
    finally { setSpLoading(false); }
  }, []);

  const copyAiosellMsg = (p, kind) => {
    const codes = `hotelCode: ${p.aiosell_hotel_code || "—"} | room: ${p.aiosell_room_code || "—"}${p.aiosell_rate_plan_code ? ` | rateplan: ${p.aiosell_rate_plan_code}` : ""}`;
    let msg;
    if (kind === "disable") {
      msg = `Please DISABLE dynamic pricing — ${p.name}\n${codes}\nThe customer has opted out.`;
    } else {
      msg =
`Dynamic pricing setup — ${p.name}
${codes}
Guest type: ${p.dp_property_type || "—"}
Peak months: ${(p.dp_peak_months || []).join(", ") || "—"}
Peak days: ${(p.dp_peak_days || []).join(", ") || "—"}
Min threshold: ₹${(p.min_price || 0).toLocaleString("en-IN")}
Max: ₹${(p.max_price || 0).toLocaleString("en-IN")}
Please enable dynamic pricing for this property.`;
    }
    navigator.clipboard.writeText(msg)
      .then(() => toast.success(kind === "disable" ? "Disable message copied — paste in the Aiosell group" : "Setup message copied — paste in the Aiosell group"))
      .catch(() => toast.error("Could not copy"));
  };

  const handleActivatePricing = async (propId) => {
    setSpDoing(s => ({ ...s, [propId]: true }));
    try {
      await api.post(`/admin/aiosell/activate-pricing/${propId}`);
      toast.success("Smart Pricing activated — host notified");
      setSpProps(prev => prev.map(p => p.id === propId ? { ...p, aiosell_pricing_status: "active" } : p));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to activate"); }
    finally { setSpDoing(s => ({ ...s, [propId]: false })); }
  };

  const sendToWhatsApp = (prop) => {
    const submitted = prop.airbnb_submitted_at
      ? fmtSubmitted(prop.airbnb_submitted_at)
      : "—";
    const msg =
`🏠 Airbnb Connection Request
Property: ${prop.name}${prop.city ? ` (${prop.city})` : ""}
Host: ${prop.host_name} · ${prop.host_email}
Hotel Code: ${prop.aiosell_hotel_code || "—"}
Airbnb User ID: ${prop.airbnb_user_id}
Code: ${prop.airbnb_code}
Submitted: ${submitted}

Please link this Airbnb account to the hotel code above. Thank you!`;
    navigator.clipboard.writeText(msg).then(() => {
      toast.success("Message copied — paste it in the Aiosell WhatsApp group");
    });
    window.open("https://web.whatsapp.com", "_blank");
  };

  const handleAbDone = async (propId) => {
    setAbDoing(s => ({ ...s, [propId]: true }));
    try {
      await api.post(`/admin/aiosell/airbnb-done/${propId}`);
      toast.success("Marked as done");
      setAbProps(prev => prev.filter(p => p.id !== propId));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setAbDoing(s => ({ ...s, [propId]: false })); }
  };

  const handleSaveCodes = async (propId) => {
    const codes = cmCodes[propId] || {};
    if (!codes.hotel || !codes.room) { toast.error("Hotel code and room code are required"); return; }
    setCmSaving(propId);
    try {
      await api.put(`/admin/aiosell/codes/${propId}`, {
        aiosell_hotel_code:     codes.hotel,
        aiosell_room_code:      codes.room,
        aiosell_rate_plan_code: codes.rate,
      });
      toast.success("Codes saved — channel manager is now active for this property");
      fetchCmPending();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save codes"); }
    finally { setCmSaving(null); }
  };

  const fetchUsers = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}`);
      setUsers(data.users || []);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(); fetchCmPending(); fetchAbPending(); fetchSpRequests(); fetchReported(false); fetchAiUsage(30); fetchEngagement(30); fetchAdoption();
    setStatsLoading(true);
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}).finally(() => setStatsLoading(false));
  }, [fetchUsers, fetchCmPending, fetchAbPending, fetchSpRequests, fetchReported, fetchAiUsage, fetchEngagement, fetchAdoption]);

  useEffect(() => {
    const t = setTimeout(() => fetchUsers(search), 350);
    return () => clearTimeout(t);
  }, [search, fetchUsers]);

  const handleSetPlan = async (email, plan) => {
    if (!plan) return;
    const trialDays = parseInt(pendingTrial[email]) || 0;
    setSaving(email);
    try {
      await api.post("/admin/set-plan", { email, plan, trial_days: trialDays });
      toast.success(trialDays > 0 ? `${email} → ${plan} (${trialDays}-day trial)` : `${email} → ${plan}`);
      setUsers(prev => prev.map(u => u.email === email ? { ...u, plan } : u));
      setPendingPlan(p => { const n = { ...p }; delete n[email]; return n; });
      setPendingTrial(p => { const n = { ...p }; delete n[email]; return n; });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update plan");
    } finally {
      setSaving(null);
    }
  };

  const totalByPlan = PLAN_OPTIONS.reduce((acc, p) => {
    acc[p.value] = users.filter(u => u.plan === p.value).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-red-100 flex items-center justify-center shrink-0">
          <ShieldCheck size={20} weight="duotone" className="text-red-600" />
        </div>
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Admin Panel</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Manage customer accounts and plans. Visible only to admins.</p>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Users",       val: statsLoading ? "…" : (stats?.total_users ?? (totalByPlan ? Object.values(totalByPlan).reduce((a,b)=>a+b,0) : "—")), sub: `+${statsLoading ? "…" : (stats?.new_users_30d ?? "—")} this month`, Icon: UsersThree, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-900/20" },
          { label: "Properties",        val: statsLoading ? "…" : (stats?.total_properties ?? "—"), sub: "across all hosts", Icon: House, color: "text-violet-500", bg: "bg-violet-50 dark:bg-violet-900/20" },
          { label: "Total Bookings",    val: statsLoading ? "…" : (stats?.total_bookings ?? "—"), sub: "all time", Icon: CalendarCheck, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-900/20" },
          { label: "Monthly Revenue",   val: statsLoading ? "…" : (stats ? `₹${(stats.monthly_revenue/100000).toFixed(1)}L` : "—"), sub: stats && !statsLoading ? `₹${(stats.total_revenue/100000).toFixed(1)}L total` : "tracked bookings", Icon: CurrencyInr, color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-900/20" },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 shadow-sm flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center shrink-0`}>
              <s.Icon size={18} weight="duotone" className={s.color} />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 dark:text-white leading-none">{s.val}</p>
              <p className="text-[10px] text-slate-400 mt-0.5 truncate">{s.label}</p>
              <p className="text-[10px] text-slate-300 dark:text-slate-500 truncate">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Plan breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {PLAN_OPTIONS.map(p => {
          const Icon = PLAN_ICON[p.value] || Sparkle;
          return (
            <div key={p.value} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl p-3 text-center shadow-sm">
              <Icon size={16} weight="duotone" className={`mx-auto mb-1 ${p.color}`} />
              <p className="text-base font-bold text-slate-900 dark:text-white">{totalByPlan[p.value] || 0}</p>
              <p className="text-[10px] text-slate-400 capitalize">{p.value}</p>
            </div>
          );
        })}
      </div>

      {/* Channel Manager Queue */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <LinkSimple size={16} weight="duotone" className="text-amber-500" />
          <div>
            <p className="font-semibold text-slate-800 dark:text-white text-sm">Channel Manager Setup Queue</p>
            <p className="text-xs text-slate-400">Properties waiting for Aiosell hotel + room codes</p>
          </div>
          <button onClick={fetchCmPending} className="ml-auto text-xs text-slate-400 hover:text-slate-600 transition-colors">Refresh</button>
        </div>

        {cmLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-amber-300 border-t-amber-500 rounded-full animate-spin mx-auto" /></div>
        ) : cmProps.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-300" weight="fill" />
            <p className="text-sm">No pending setups — all enabled properties have codes</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {cmProps.map(p => {
              const codes = cmCodes[p.id] || { hotel: "", room: "", rate: "" };
              const isComplete = !p.codes_missing;
              // Codes present only proves OUR leg. A green badge is reserved for a property
              // that has actually received a booking from an OTA, since Aiosell exposes no
              // API to list linked channels — an arrived reservation is the only real proof.
              const LINK_BADGE = {
                codes_missing: { label: "Codes missing", cls: "bg-amber-50 text-amber-600 border-amber-200", Icon: WarningCircle },
                push_failing:  { label: "Push failing",  cls: "bg-red-50 text-red-600 border-red-200",       Icon: WarningCircle },
                no_ota:        { label: "No OTA connected", cls: "bg-slate-100 text-slate-600 border-slate-300", Icon: WarningCircle },
                live:          { label: "Live",          cls: "bg-emerald-50 text-emerald-600 border-emerald-200", Icon: CheckCircle },
              };
              const badge = LINK_BADGE[p.link_status] || LINK_BADGE[isComplete ? "no_ota" : "codes_missing"];
              const BadgeIcon = badge.Icon;
              return (
                <div key={p.id} className="px-5 py-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 dark:text-white text-sm">{p.name}</p>
                        <span className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${badge.cls}`}>
                          <BadgeIcon size={9} weight="fill" /> {badge.label}
                        </span>
                        {p.link_status === "live" && p.ota_channels?.length > 0 && (
                          <span className="text-[10px] font-medium text-slate-500 dark:text-slate-300">
                            {p.ota_channels.join(", ")} · {p.ota_booking_count} booking{p.ota_booking_count === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{p.host_name} · {p.host_email} · {p.city || ""}</p>
                      {p.link_status === "no_ota" && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">
                          Codes set and pushes accepted, but no booking has ever arrived from an OTA. The host still needs to connect their channels.
                        </p>
                      )}
                      {p.link_status === "push_failing" && p.aiosell_last_sync_error && (
                        <p className="text-[11px] text-red-500 mt-1">Last sync failed: {p.aiosell_last_sync_error}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Hotel Code <span className="text-red-400">*</span></label>
                      <input
                        value={codes.hotel}
                        onChange={e => setCmCodes(c => ({ ...c, [p.id]: { ...c[p.id], hotel: e.target.value } }))}
                        placeholder="e.g. HTL-12345"
                        className="mt-1 w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Room Code <span className="text-red-400">*</span></label>
                      <input
                        value={codes.room}
                        onChange={e => setCmCodes(c => ({ ...c, [p.id]: { ...c[p.id], room: e.target.value } }))}
                        placeholder="e.g. RM-001"
                        className="mt-1 w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Rate Plan Code</label>
                      <input
                        value={codes.rate}
                        onChange={e => setCmCodes(c => ({ ...c, [p.id]: { ...c[p.id], rate: e.target.value } }))}
                        placeholder="e.g. RP-BAR"
                        className="mt-1 w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 font-mono"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleSaveCodes(p.id)}
                    disabled={cmSaving === p.id}
                    className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold text-xs px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {cmSaving === p.id ? "Saving…" : isComplete ? "Update codes" : "Save codes & activate"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Engagement — who is actually using HostFolio, and when */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <UsersThree size={16} weight="duotone" className="text-emerald-500" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 dark:text-white text-sm">Engagement</p>
            <p className="text-xs text-slate-400">Logins by day, IST. Who came back and who stopped.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => { setEngDays(d); fetchEngagement(d); }}
                className={`min-h-[44px] px-3 text-xs rounded-xl border transition-colors ${
                  engDays === d
                    ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 text-emerald-700 dark:text-emerald-300 font-semibold"
                    : "border-slate-200 dark:border-slate-600 text-slate-500 hover:text-slate-700"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {engLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-emerald-300 border-t-emerald-500 rounded-full animate-spin mx-auto" /></div>
        ) : !eng ? (
          <div className="py-10 text-center text-slate-400 text-sm">No data</div>
        ) : (
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Users", value: eng.totals?.total_users ?? 0 },
                { label: "MAU", value: eng.totals?.mau ?? 0 },
                { label: "WAU", value: eng.totals?.wau ?? 0 },
                { label: "Avg DAU", value: eng.totals?.dau_avg ?? 0 },
                { label: "Never logged in", value: eng.totals?.never_logged_in ?? 0 },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-slate-100 dark:border-slate-700 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{s.label}</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white mt-0.5">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Daily logins. Zero days are rendered as empty slots on purpose — a
                gap-free axis is what makes a decline visible. */}
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Logins per day
              </p>
              <div className="overflow-x-auto">
                <div className="flex items-end gap-[2px] h-24 min-w-[280px]">
                  {(eng.daily || []).map(d => {
                    const max = Math.max(1, ...(eng.daily || []).map(x => x.logins));
                    return (
                      <div key={d.date} title={`${d.date}: ${d.logins} login${d.logins === 1 ? "" : "s"}, ${d.users} user${d.users === 1 ? "" : "s"}`}
                        className="flex-1 min-w-[4px] bg-emerald-400/80 dark:bg-emerald-500/70 rounded-t-sm"
                        style={{ height: `${Math.round((d.logins / max) * 100)}%` }} />
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>{eng.daily?.[0]?.date}</span>
                <span>{eng.daily?.[eng.daily.length - 1]?.date}</span>
              </div>
            </div>

            {/* Time on site. Separate measurement from logins, and only exists from the
                day tracking was deployed — so say so rather than show a bare zero. */}
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Time on site
              </p>
              {!tos || (tos.totals?.people ?? 0) === 0 ? (
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  No time tracked yet. This measures from the day it was deployed, so it
                  stays empty until hosts next use the app.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "People", value: tos.totals.people },
                      { label: "Total hours", value: tos.totals.total_hours },
                      { label: "Visits", value: tos.totals.visits },
                      { label: "Avg visit", value: `${tos.totals.avg_visit_min} min` },
                    ].map(s => (
                      <div key={s.label} className="rounded-xl border border-slate-100 dark:border-slate-700 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">{s.label}</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-white mt-0.5">{s.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-slate-400 text-left">
                          <th className="font-medium py-1 pr-3">Person</th>
                          <th className="font-medium py-1 pr-3 text-right">Time</th>
                          <th className="font-medium py-1 pr-3 text-right">Visits</th>
                          <th className="font-medium py-1 text-right">Avg visit</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700 dark:text-slate-200">
                        {tos.per_person.slice(0, 10).map(p => (
                          <tr key={p.who} className="border-t border-slate-50 dark:border-slate-700/50">
                            <td className="py-1.5 pr-3 break-all">
                              {p.email}
                              {p.kind === "staff" && <span className="text-slate-400"> · staff</span>}
                            </td>
                            <td className="py-1.5 pr-3 text-right whitespace-nowrap">
                              {p.minutes >= 60 ? `${p.hours}h` : `${p.minutes}m`}
                            </td>
                            <td className="py-1.5 pr-3 text-right">{p.visits}</td>
                            <td className="py-1.5 text-right whitespace-nowrap">{p.avg_visit_min}m</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">{tos.note}</p>
                </>
              )}
            </div>

            {(eng.dormant_14d || []).length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  Dormant 14 days or more ({eng.dormant_14d.length})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-slate-400 text-left">
                        <th className="font-medium py-1 pr-3">Host</th>
                        <th className="font-medium py-1 pr-3">Plan</th>
                        <th className="font-medium py-1 pr-3 text-right">Logins</th>
                        <th className="font-medium py-1 text-right">Last seen</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700 dark:text-slate-200">
                      {eng.dormant_14d.slice(0, 10).map(r => (
                        <tr key={r.user_id} className="border-t border-slate-50 dark:border-slate-700/50">
                          <td className="py-1.5 pr-3 break-all">{r.email}</td>
                          <td className="py-1.5 pr-3">
                            <span className={`px-1.5 py-0.5 rounded ${PLAN_BADGE[r.plan] || PLAN_BADGE.free}`}>{r.plan}</span>
                          </td>
                          <td className="py-1.5 pr-3 text-right">{r.logins}</td>
                          <td className="py-1.5 text-right whitespace-nowrap">{r.days_since_login}d ago</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Without this the login count reads as a visit count, which it is not. */}
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-4 leading-relaxed">
              {eng.caveat} History from {eng.totals?.history_from}.
            </p>
          </div>
        )}
      </div>

      {/* Feature adoption — what each host has never once opened */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <House size={16} weight="duotone" className="text-sky-500" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 dark:text-white text-sm">Feature adoption</p>
            <p className="text-xs text-slate-400">Last 90 days. What to show each host next.</p>
          </div>
        </div>

        {adoptLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-sky-300 border-t-sky-500 rounded-full animate-spin mx-auto" /></div>
        ) : !adopt ? (
          <div className="py-10 text-center text-slate-400 text-sm">No data</div>
        ) : (adopt.totals?.people_with_activity ?? 0) === 0 ? (
          <div className="px-5 py-4">
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Nothing tracked yet. Adoption measures from the day tracking was deployed, so
              this fills in as hosts use the app. {adopt.totals?.hosts_with_no_activity ?? 0} hosts
              have no activity recorded.
            </p>
          </div>
        ) : (
          <div className="px-5 py-4">
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-slate-400 text-left">
                    <th className="font-medium py-1 pr-3">Feature</th>
                    <th className="font-medium py-1 pr-3 text-right">Hosts</th>
                    <th className="font-medium py-1">Share</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700 dark:text-slate-200">
                  {(adopt.by_feature || []).map(f => (
                    <tr key={f.feature} className="border-t border-slate-50 dark:border-slate-700/50">
                      <td className="py-1.5 pr-3 capitalize">{f.feature}</td>
                      <td className="py-1.5 pr-3 text-right">{f.hosts}</td>
                      <td className="py-1.5 min-w-[70px]">
                        <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                          <div className="h-full bg-sky-400 dark:bg-sky-500 rounded-full"
                            style={{ width: `${f.pct_of_hosts}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* The actionable half: per host, what they have never touched. */}
            <div className="mt-4 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Never opened, by host
              </p>
              {(adopt.per_person || []).filter(p => p.kind === "host").slice(0, 8).map(p => (
                <div key={p.who} className="rounded-xl border border-slate-100 dark:border-slate-700 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-medium text-slate-700 dark:text-slate-200 break-all">{p.email}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${PLAN_BADGE[p.plan] || PLAN_BADGE.free}`}>{p.plan}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{p.coverage_pct}% of features</span>
                  </div>
                  {p.never_used.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {p.never_used.map(f => (
                        <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 capitalize">
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">{adopt.note}</p>
          </div>
        )}
      </div>

      {/* AI usage (Gemini) */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <SparkleAi size={16} weight="duotone" className="text-violet-500" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 dark:text-white text-sm">AI Usage (Gemini)</p>
            <p className="text-xs text-slate-400">Token counts are exact. Cost is indicative.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => { setAiDays(d); fetchAiUsage(d); }}
                className={`min-h-[44px] px-3 text-xs rounded-xl border transition-colors ${
                  aiDays === d
                    ? "bg-violet-50 dark:bg-violet-900/20 border-violet-300 text-violet-700 dark:text-violet-300 font-semibold"
                    : "border-slate-200 dark:border-slate-600 text-slate-500 hover:text-slate-700"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {aiLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-violet-300 border-t-violet-500 rounded-full animate-spin mx-auto" /></div>
        ) : !aiUsage ? (
          <div className="py-10 text-center text-slate-400 text-sm">No data</div>
        ) : (
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Calls", value: (aiUsage.totals?.calls ?? 0).toLocaleString("en-IN") },
                { label: "Total tokens", value: (aiUsage.totals?.total_tokens ?? 0).toLocaleString("en-IN") },
                { label: "Est. cost", value: `$${(aiUsage.totals?.est_cost_usd ?? 0).toFixed(4)}` },
                { label: "Failed calls", value: (aiUsage.totals?.failed ?? 0).toLocaleString("en-IN") },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-slate-100 dark:border-slate-700 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{s.label}</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white mt-0.5 break-words">{s.value}</p>
                </div>
              ))}
            </div>

            {aiUsage.totals?.calls === 0 && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-3">
                No AI calls in this window. {aiUsage.lifetime_calls === 0
                  ? "Nothing has been logged since tracking was added."
                  : `${aiUsage.lifetime_calls.toLocaleString("en-IN")} calls logged all time.`}
              </p>
            )}

            {["by_feature", "by_model"].map(key => (
              (aiUsage[key] || []).length > 0 && (
                <div key={key} className="mt-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                    {key === "by_feature" ? "By feature" : "By model"}
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-slate-400 text-left">
                          <th className="font-medium py-1 pr-3">{key === "by_feature" ? "Feature" : "Model"}</th>
                          <th className="font-medium py-1 pr-3 text-right">Calls</th>
                          <th className="font-medium py-1 pr-3 text-right">Tokens</th>
                          <th className="font-medium py-1 text-right">Est. cost</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700 dark:text-slate-200">
                        {(aiUsage[key] || []).slice(0, 8).map(r => (
                          <tr key={r.name} className="border-t border-slate-50 dark:border-slate-700/50">
                            <td className="py-1.5 pr-3 font-mono break-all">{r.name}</td>
                            <td className="py-1.5 pr-3 text-right">{r.calls.toLocaleString("en-IN")}</td>
                            <td className="py-1.5 pr-3 text-right">{r.total_tokens.toLocaleString("en-IN")}</td>
                            <td className="py-1.5 text-right">${r.est_cost_usd.toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Reported OTA bookings — hosts waiting on us to cancel with the booking site */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <Flag size={16} weight="duotone" className="text-red-500" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 dark:text-white text-sm">
              Reported Bookings
              {rpRows.filter(r => r.needs_ota_cancellation).length > 0 && (
                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
                  {rpRows.filter(r => r.needs_ota_cancellation).length} open
                </span>
              )}
            </p>
            <p className="text-xs text-slate-400">Hosts told us something is wrong. We cancel with the OTA, then confirm back to them.</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={rpShowDone}
                onChange={e => { setRpShowDone(e.target.checked); fetchReported(e.target.checked); }}
                className="rounded border-slate-300" />
              Show closed
            </label>
            <button onClick={() => fetchReported(rpShowDone)} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Refresh</button>
          </div>
        </div>

        {rpLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin mx-auto" /></div>
        ) : rpRows.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-300" weight="fill" />
            <p className="text-sm">Nothing reported</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {rpRows.map(b => {
              const bid = b.id || b.booking_id;
              const open = b.needs_ota_cancellation;
              return (
                <div key={bid} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-slate-800 dark:text-white text-sm truncate">{b.guest_name || "Guest"}</p>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0">
                      {(b.platform || "OTA").toUpperCase()}
                    </span>
                    {open
                      ? <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 shrink-0"><WarningCircle size={9} weight="fill" /> Needs cancelling</span>
                      : <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200 shrink-0">Closed: {b.ota_cancellation_outcome || "—"}</span>}
                    {b.ota_confirmed_cancelled && (
                      <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0"><CheckCircle size={9} weight="fill" /> OTA confirmed cancelled</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                    {b.property_name} · {b.host_name} · {b.host_email}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">
                    {b.check_in} to {b.check_out} · ₹{(b.total_amount || 0).toLocaleString("en-IN")}
                    {b.aiosell_hotel_code && <> · Hotel <span className="font-mono">{b.aiosell_hotel_code}</span></>}
                    {b.aiosell_booking_id && <> · Ref <span className="font-mono">{b.aiosell_booking_id}</span></>}
                    {b.host_reviewed_at && <> · reported {fmtSubmitted(b.host_reviewed_at)}</>}
                  </p>
                  {b.host_review_reason && (
                    <div className="mt-2 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                      <div className="text-[9px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-300">Host said</div>
                      <div className="text-[11px] text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">{b.host_review_reason}</div>
                    </div>
                  )}
                  {!open && b.ota_cancellation_note && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">Note: {b.ota_cancellation_note}</p>
                  )}
                  {open && (
                    <div className="mt-2.5 flex flex-col gap-2">
                      <input
                        value={rpNote[bid] || ""}
                        onChange={e => setRpNote(n => ({ ...n, [bid]: e.target.value }))}
                        placeholder="What happened? Required if the OTA refused."
                        className="w-full text-base sm:text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-slate-400"
                      />
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button onClick={() => resolveReported(b, "cancelled")} disabled={rpDoing[bid]}
                          className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl font-semibold disabled:opacity-50">
                          Cancelled with the OTA
                        </button>
                        <button onClick={() => resolveReported(b, "refused")} disabled={rpDoing[bid]}
                          className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-red-600 dark:text-red-400 rounded-xl border border-red-200 dark:border-red-800 font-semibold disabled:opacity-50">
                          OTA refused
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Smart Pricing Requests */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <CurrencyInr size={16} weight="duotone" className="text-amber-500" />
          <div>
            <p className="font-semibold text-slate-800 dark:text-white text-sm">Smart Pricing Requests</p>
            <p className="text-xs text-slate-400">Hosts who switched on Aiosell dynamic pricing (+₹500/property/mo)</p>
          </div>
          <button onClick={fetchSpRequests} className="ml-auto text-xs text-slate-400 hover:text-slate-600 transition-colors">Refresh</button>
        </div>

        {spLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-amber-300 border-t-amber-500 rounded-full animate-spin mx-auto" /></div>
        ) : spProps.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-300" weight="fill" />
            <p className="text-sm">No Smart Pricing requests</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {spProps.map(p => {
              const isActive = p.aiosell_pricing_status === "active";
              const requestedAt = p.aiosell_pricing_requested_at
                ? new Date(p.aiosell_pricing_requested_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })
                : null;
              return (
                <div key={p.id} className="px-5 py-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 dark:text-white text-sm truncate">{p.name}</p>
                      {isActive
                        ? <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0"><CheckCircle size={9} weight="fill" /> Live</span>
                        : <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 shrink-0"><WarningCircle size={9} weight="fill" /> Requested</span>
                      }
                      {p.autopay_active
                        ? <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0"><CurrencyInr size={9} weight="bold" /> Auto-pay on</span>
                        : <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200 shrink-0"><WarningCircle size={9} weight="fill" /> No auto-pay</span>
                      }
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{p.host_name} · {p.host_email} · {p.city || ""}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">
                      Band: <span className="font-mono">₹{(p.min_price || 0).toLocaleString("en-IN")}–₹{(p.max_price || 0).toLocaleString("en-IN")}</span>
                      {p.aiosell_hotel_code && <> · Hotel: <span className="font-mono">{p.aiosell_hotel_code}</span></>}
                      {requestedAt && <> · {requestedAt}</>}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0 self-center">
                    {isActive ? (
                      <span className="text-xs text-emerald-500 font-semibold">Active ✓</span>
                    ) : (
                      <button
                        onClick={() => handleActivatePricing(p.id)}
                        disabled={spDoing[p.id]}
                        className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold text-xs px-4 py-2 rounded-xl transition-colors disabled:opacity-50 w-full"
                      >
                        {spDoing[p.id] ? "Activating…" : "Activate"}
                      </button>
                    )}
                    <button
                      onClick={() => copyAiosellMsg(p, "setup")}
                      className="flex items-center gap-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <Copy size={11} weight="bold" /> Copy setup msg
                    </button>
                    <button
                      onClick={() => copyAiosellMsg(p, "disable")}
                      className="text-[10px] text-slate-400 hover:text-red-500 underline underline-offset-2"
                    >
                      Copy disable msg
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Airbnb Connection Queue */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <WhatsappLogo size={16} weight="duotone" className="text-green-500" />
          <div>
            <p className="font-semibold text-slate-800 dark:text-white text-sm">Airbnb Connection Queue</p>
            <p className="text-xs text-slate-400">Hosts waiting for Airbnb → Aiosell linking</p>
          </div>
          <button onClick={fetchAbPending} className="ml-auto text-xs text-slate-400 hover:text-slate-600 transition-colors">Refresh</button>
        </div>

        {/* Record an authorization on a host's behalf (support-assisted phone setup) */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30">
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">Record authorization for a host</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <input
              value={abForm.email} placeholder="Host account email" autoCapitalize="none" spellCheck={false}
              onChange={e => setAbForm(f => ({ ...f, email: e.target.value }))}
              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-green-400"
            />
            <input
              value={abForm.airbnb_user_id} placeholder="Airbnb User ID" inputMode="numeric" spellCheck={false} maxLength={100}
              onChange={e => setAbForm(f => ({ ...f, airbnb_user_id: e.target.value }))}
              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 font-mono focus:outline-none focus:border-green-400"
            />
            <input
              value={abForm.airbnb_code} placeholder="Code" autoCapitalize="none" spellCheck={false} maxLength={2048}
              onChange={e => setAbForm(f => ({ ...f, airbnb_code: e.target.value }))}
              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 font-mono focus:outline-none focus:border-green-400"
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={recordAirbnbAuth}
              disabled={abRecording || !abForm.email.trim() || !abForm.airbnb_user_id.trim() || !abForm.airbnb_code.trim()}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
            >
              {abRecording ? "Recording…" : "Record authorization"}
            </button>
            <p className="text-[11px] text-slate-400">Applies to every channel-manager-enabled property on that account, since one authorization covers the whole Airbnb account.</p>
          </div>
        </div>

        {abLoading ? (
          <div className="py-10 text-center"><div className="w-5 h-5 border-2 border-green-300 border-t-green-500 rounded-full animate-spin mx-auto" /></div>
        ) : abProps.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-300" weight="fill" />
            <p className="text-sm">No pending Airbnb connections</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {abProps.map(p => {
              const submitted = p.airbnb_submitted_at
                ? fmtSubmitted(p.airbnb_submitted_at)
                : "—";
              return (
                <div key={p.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-white text-sm">{p.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.host_name} · {p.host_email}{p.city ? ` · ${p.city}` : ""}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Submitted: {submitted}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Hotel Code</p>
                      <p className="text-xs font-mono text-slate-700 dark:text-slate-200 mt-0.5">{p.aiosell_hotel_code || <span className="text-amber-500">Not set</span>}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Airbnb User ID</p>
                      <p className="text-xs font-mono text-slate-700 dark:text-slate-200 mt-0.5">{p.airbnb_user_id}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Code</p>
                      <p className="text-xs font-mono text-slate-700 dark:text-slate-200 mt-0.5 truncate">{p.airbnb_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => sendToWhatsApp(p)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-xl transition"
                    >
                      <WhatsappLogo size={13} /> Send to WhatsApp
                    </button>
                    <button
                      onClick={() => {
                        const txt = `${p.airbnb_user_id}\n${p.airbnb_code}`;
                        navigator.clipboard.writeText(txt);
                        toast.success("User ID + Code copied");
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl transition"
                    >
                      <Copy size={12} /> Copy codes only
                    </button>
                    <button
                      onClick={() => handleAbDone(p.id)}
                      disabled={!!abDoing[p.id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl transition disabled:opacity-50"
                    >
                      {abDoing[p.id] ? <div className="w-3 h-3 border-2 border-emerald-400/40 border-t-emerald-600 rounded-full animate-spin" /> : <><CheckCircle size={12} /> Mark done</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
        />
      </div>

      {/* User table */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-6 h-6 border-2 border-amber-300 border-t-amber-500 rounded-full animate-spin mx-auto" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <UserCircle size={40} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No users found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {/* Table header */}
            <div className="hidden md:grid grid-cols-[1fr_180px_160px_120px] gap-4 px-5 py-3 bg-slate-50 dark:bg-slate-900/50 text-[11px] font-bold text-slate-400 uppercase tracking-wide">
              <span>User</span>
              <span>Current Plan</span>
              <span>Change Plan</span>
              <span></span>
            </div>

            {users.map((u, i) => {
              const Icon = PLAN_ICON[u.plan] || Sparkle;
              const selected = pendingPlan[u.email] ?? u.plan;
              const trialDays = parseInt(pendingTrial[u.email]) || 0;
              const changed  = selected !== u.plan || trialDays > 0;

              return (
                <motion.div key={u.user_id || u.email}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="grid grid-cols-1 md:grid-cols-[1fr_180px_160px_120px] gap-3 md:gap-4 items-center px-5 py-4">

                  {/* User info */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 text-amber-700 font-bold text-sm">
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{u.name || "—"}</p>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                  </div>

                  {/* Current plan badge + trial countdown */}
                  <div className="flex flex-col items-start gap-1">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>
                      <Icon size={11} weight="fill" />
                      {u.plan || "free"}
                    </span>
                    {u.trial_ends_at && u.plan === "free" && (
                      <span className="text-[10px] text-slate-400">trial</span>
                    )}
                    {u.plan_is_trial && u.plan_expires_at && u.plan !== "free" && (() => {
                      const days = Math.max(0, Math.ceil((new Date(u.plan_expires_at) - Date.now()) / 86400000));
                      return (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
                          ⏳ {days}d trial left · ends {new Date(u.plan_expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        </span>
                      );
                    })()}
                    {!u.plan_is_trial && u.plan !== "free" && u.plan !== "admin" && (
                      <span className="text-[10px] text-slate-400">permanent (no trial)</span>
                    )}
                  </div>

                  {/* Plan selector + optional trial length */}
                  <div className="space-y-1.5">
                    <select
                      value={selected}
                      onChange={e => setPendingPlan(p => ({ ...p, [u.email]: e.target.value }))}
                      className="text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:border-amber-400 w-full"
                    >
                      {PLAN_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={pendingTrial[u.email] ?? ""}
                      onChange={e => setPendingTrial(p => ({ ...p, [u.email]: e.target.value }))}
                      placeholder="Trial days (e.g. 90) — blank = permanent"
                      title="Grant this plan free for N days, then auto-downgrade to Free. Leave blank for a permanent (non-expiring) override."
                      className="text-[11px] px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:border-amber-400 w-full"
                    />
                  </div>

                  {/* Apply button */}
                  <button
                    onClick={() => handleSetPlan(u.email, selected)}
                    disabled={!changed || saving === u.email}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all w-full md:w-auto ${
                      changed
                        ? "bg-amber-400 hover:bg-amber-500 text-slate-900 shadow-sm"
                        : "bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                    } disabled:opacity-60`}
                  >
                    {saving === u.email
                      ? <span className="flex items-center justify-center gap-1"><div className="w-3 h-3 border-2 border-slate-400 border-t-slate-700 rounded-full animate-spin" />Saving</span>
                      : changed ? "Apply" : "No change"
                    }
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 text-center">
        Showing up to 50 users · Changes take effect immediately · This page is not visible to non-admin accounts
      </p>
    </div>
  );
}
